import { MagnifyingGlass, Package, Trash, WarningCircle } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { MONO_CLASS, Mono } from '../../ui/Mono';
import QtyLines from './QtyLines';
import { BuildDraftLines } from './repackModel';

/**
 * O PALLET DE ORIGEM — carrega o LPN pelo código e mostra o conteúdo com um
 * campo "quanto sai" por item.
 *
 * ── Por que o item é (produto + LOTE), e não só o produto ───────────────────
 * O saldo do §3 tem essa identidade: o mesmo produto em dois lotes de
 * fornecedor são duas linhas, e é essa distinção que o recall do §6.7 percorre.
 * Por isso cada item vira uma linha própria e o lote viaja sempre no wire — o
 * backend recusa (409 `SUPPLIER_LOT_AMBIGUOUS`) quando o produto está em mais
 * de um lote e ninguém diz qual sai, e ele está certo em recusar.
 *
 * ⚠️ "Tem no pallet" é o número que o BACKEND devolveu no item. A tela NÃO
 * mostra nenhum "vai sobrar": saldo depois é assunto da prévia, que é a conta
 * do servidor. Ter duas contas na tela seria ter duas verdades.
 */
/**
 * Carrega o pallet e devolve o rascunho pronto pelo `onChange`. Exportado
 * porque o aviso de FIFO também troca o pallet de origem com um clique — e
 * duas rotinas de carga divergiriam na primeira correção.
 */
export async function LoadSourceLpn({ code, label, onChange }) {
    const normalized = String(code || '')
        .trim()
        .toUpperCase();
    if (!normalized) {
        return;
    }
    onChange({ code: normalized, loading: true, loadError: null });
    try {
        const lpn = await api.getWarehouseLpn(normalized);
        onChange({
            loading: false,
            loadError: null,
            code: lpn?.code || normalized,
            lpn,
            lines: BuildDraftLines(lpn?.items || []),
        });
    } catch (error) {
        onChange({
            loading: false,
            lpn: null,
            lines: [],
            // ⚠️ Ler o pallet é `GET /warehouse/lpns/:code`, que exige
            // `wms:manage:lpns` — permissão IRMÃ de `wms:manage:repack`, não
            // implicada por ela. Quem receber só a granular do repack esbarra
            // aqui, e a mensagem tem que dizer O QUE pedir ao administrador em
            // vez de mostrar um 403 cru. (Acoplamento relatado ao dono.)
            loadError:
                error?.status === 403
                    ? label('wms.repack.source_forbidden')
                    : error?.message || label('wms.common.load_error'),
        });
    }
}

const SourceLpnPanel = ({
    label,
    source,
    onChange,
    onRemove = null,
    disabled = false,
    title = null,
    showLines = true,
    autoFocus = false,
}) => {
    const load = () => LoadSourceLpn({ code: source.code, label, onChange });

    const changeLine = (key, qtyText) => {
        onChange({ lines: source.lines.map((line) => (line.key === key ? { ...line, qtyText } : line)) });
    };

    const reset = () => onChange({ code: '', lpn: null, lines: [], loadError: null });

    return (
        <div className="space-y-3 rounded-xl border border-mono-200 p-4 dark:border-mono-800">
            <div className="flex items-center gap-2">
                <Package size={18} className="shrink-0 text-mono-500" />
                <h3 className="flex-1 text-sm font-bold text-black dark:text-white">
                    {title || label('wms.repack.source_title')}
                </h3>
                {onRemove ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onRemove}
                        disabled={disabled}
                        aria-label={label('wms.repack.remove_source')}
                        title={label('wms.repack.remove_source')}
                    >
                        <Trash size={16} />
                    </Button>
                ) : null}
            </div>

            {source.lpn ? (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Mono className="text-lg font-bold text-black dark:text-white">{source.lpn.code}</Mono>
                        <Badge variant={source.lpn.status === 'ACTIVE' ? 'success' : 'warning'}>
                            {source.lpn.status}
                        </Badge>
                        {source.lpn.location_code ? (
                            <Badge variant="neutral">{source.lpn.location_code}</Badge>
                        ) : null}
                        {source.lpn.deposit_code ? <Badge variant="neutral">{source.lpn.deposit_code}</Badge> : null}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={reset}
                            disabled={disabled}
                        >
                            {label('wms.repack.change_lpn')}
                        </Button>
                    </div>

                    {source.lpn.status === 'CLOSED' ? (
                        <p className="flex items-start gap-1.5 rounded-lg bg-warning-soft px-3 py-2 text-xs font-medium text-warning-text dark:bg-warning-900/30 dark:text-warning-200">
                            <WarningCircle size={14} className="mt-0.5 shrink-0" />
                            {label('wms.repack.closed_lpn')}
                        </p>
                    ) : null}

                    {showLines ? (
                        <QtyLines
                            label={label}
                            lines={source.lines}
                            onChangeQty={changeLine}
                            disabled={disabled}
                            emptyText={label('wms.repack.no_items')}
                        />
                    ) : null}
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.repack.source_hint')}</p>
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <Input
                                label={label('wms.repack.lpn_code')}
                                value={source.code}
                                onChange={(event) => onChange({ code: event.target.value.toUpperCase() })}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        load();
                                    }
                                }}
                                placeholder="MP-260811-001"
                                autoComplete="off"
                                autoFocus={autoFocus}
                                disabled={disabled || source.loading}
                                className={MONO_CLASS}
                                errorMessage={source.loadError || undefined}
                            />
                        </div>
                        <Button
                            type="button"
                            onClick={load}
                            loading={source.loading}
                            disabled={disabled || !source.code.trim()}
                        >
                            <MagnifyingGlass size={16} className="mr-2" />
                            {label('wms.repack.load_lpn')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SourceLpnPanel;
